import { toast } from "sonner";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, Check, X, Swords, Wind, Hand, Inbox, Users, Flame } from "lucide-react";

const WORK_ICON = {
  "Hard Sparring": Swords,
  "Technical/Flow Sparring": Wind,
  "Padwork/Clinch": Hand,
};

const STATUS_STYLE = {
  pending: "text-yellow-400 border-yellow-400/40 bg-yellow-400/5",
  accepted: "text-volt border-volt/40 bg-volt/5",
  declined: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5",
};

function typeMeta(r) {
  if (r.type === "stable_invite") return { icon: Users, label: "Stable invite" };
  if (r.type === "camp_invite") return { icon: Flame, label: `Camp: ${r.camp_name || "Sparring"}` };
  return { icon: WORK_ICON[r.work_type] || Swords, label: r.work_type };
}

function Row({ r, incoming, onRespond }) {
  const meta = typeMeta(r);
  const Icon = meta.icon;
  const name = incoming ? r.from_name : r.to_name;
  const sub = incoming ? r.from_gym : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`request-row-${r.id}`}
      className="flex items-center gap-3 p-4 bg-card border border-white/10 rounded-xl"
    >
      <div className="w-10 h-10 rounded-lg bg-[#0d0d0d] border border-[#27272a] flex items-center justify-center shrink-0">
        <Icon size={18} className="text-volt" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {incoming ? <ArrowDownLeft size={13} className="text-volt" /> : <ArrowUpRight size={13} className="text-zinc-500" />}
          <span className="font-heading uppercase tracking-wide truncate">{name}</span>
        </div>
        <div className="text-xs text-zinc-400 truncate">{meta.label}{sub ? ` · ${sub}` : ""}</div>
        {r.message ? <div className="text-xs text-zinc-500 truncate mt-0.5">“{r.message}”</div> : null}
      </div>

      {incoming && r.status === "pending" ? (
        <div className="flex gap-2 shrink-0">
          <button
            data-testid={`accept-${r.id}`}
            onClick={() => onRespond(r.id, "accepted")}
            className="w-9 h-9 flex items-center justify-center bg-volt text-black rounded-lg hover:bg-volt-hover transition-colors duration-200"
          ><Check size={16} /></button>
          <button
            data-testid={`decline-${r.id}`}
            onClick={() => onRespond(r.id, "declined")}
            className="w-9 h-9 flex items-center justify-center border border-[#27272a] text-zinc-400 rounded-lg hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors duration-200"
          ><X size={16} /></button>
        </div>
      ) : (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-lg border shrink-0 ${STATUS_STYLE[r.status]}`}>
          {r.status}
        </span>
      )}
    </motion.div>
  );
}

export default function RequestsInbox({ data, refetch }) {
  const respond = async (id, status) => {
    try {
      await api.patch(`/requests/${id}`, { status });
      toast.success(status === "accepted" ? "Match accepted." : "Request declined.");
      refetch();
    } catch {
      toast.error("Could not update request.");
    }
  };

  const received = data?.received || [];
  const sent = data?.sent || [];

  const Empty = ({ label }) => (
    <div className="flex flex-col items-center justify-center py-10 text-zinc-600">
      <Inbox size={28} className="mb-2" />
      <p className="text-sm uppercase tracking-wider">{label}</p>
    </div>
  );

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h3 className="font-heading text-2xl uppercase tracking-tight mb-3 flex items-center gap-2">
          <ArrowDownLeft size={20} className="text-volt" /> Incoming
        </h3>
        <div className="space-y-3">
          {received.length ? received.map((r) => (
            <Row key={r.id} r={r} incoming onRespond={respond} />
          )) : <Empty label="No incoming requests yet" />}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-2xl uppercase tracking-tight mb-3 flex items-center gap-2">
          <ArrowUpRight size={20} className="text-zinc-500" /> Sent
        </h3>
        <div className="space-y-3">
          {sent.length ? sent.map((r) => (
            <Row key={r.id} r={r} incoming={false} />
          )) : <Empty label="You haven't sent any requests" />}
        </div>
      </div>
    </div>
  );
}
